import produce from 'immer';
import { useReducer, useState } from 'react';
import GoogleLogin from 'react-google-login';
import { Link, useHistory } from 'react-router-dom';

import { google_client_id } from 'config';
import { roles, storeToken } from 'utils/handler';
import google from '../assets/img/google-icon.svg';
import logo from '../assets/img/saw-logo.png';
import * as authAction from '../store/actions/auth.action';
import { useDispatch } from 'react-redux';
import * as types from '../store/actions/types';

interface loginForm {
  email: string;
  password: string;
}

interface ReducerAction {
  type: string;
  name: string;
  payload?: string | boolean | Partial<loginForm>;
}
const initialState = {
  email: '',
  password: '',
};

const loginReducer = produce((state: loginForm, action: ReducerAction) => {
  const { type, name, payload } = action;
  switch (type) {
    case 'UPDATE':
      state = Object.assign(state, payload);
      return;
    case 'ON_TYPE':
      state[name] = payload;
      return;
    default:
      return;
  }
});

const errorReducer = produce((state: loginForm, action: ReducerAction) => {
  const { type, name, payload } = action;
  switch (type) {
    case 'ADD_ERROR':
      state[name] = payload;
      return;
    case 'DELETE_ERROR':
      state[name] = '';
      return;
    default:
      return;
  }
});

const LoginPage = () => {
  const [loginDetails, setLoginDetails] = useReducer(loginReducer, initialState);
  const [error, setError] = useReducer(errorReducer, initialState);
  const [showPassword, setShowPassword] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();

  const validation = {
    password(value: string) {
      const empty = !value || !value.trim();
      if (empty) setError({ type: 'ADD_ERROR', name: 'password', payload: "Password can't be empty." });
      else setError({ type: 'DELETE_ERROR', name: 'password' });
      if (empty) return true;
      else return false;
    },
    email(value: string) {
      const empty = !value || !value.trim();
      const isValid = value && !/^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/.test(value);
      if (empty) setError({ type: 'ADD_ERROR', name: 'email', payload: "Email address can't be empty." });
      else if (isValid) setError({ type: 'ADD_ERROR', name: 'email', payload: 'Email address is not valid.' });
      else setError({ type: 'DELETE_ERROR', name: 'email' });
      if (empty || isValid) return true;
      else return false;
    },
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { email, password } = loginDetails;

    let error = false;
    const emailError = validation.email(email);
    if (emailError) error = true;
    const passwordError = validation.password(password);
    if (passwordError) error = true;

    if (error) return;

    authAction.login({ password, email }).then((res: any) => {
      if (res)
        storeToken(res).then(() => {
          dispatch({ type: types.SHOW_REMODEL_ALERT, payload: true });
          history.push('/appraisal');
        });
    });
  };

  const responseGoogle = async (data: any) => {
    if (data.profileObj) {
      const profile = data.profileObj;
      const payload = {
        email: profile.email,
        provider_id: profile.googleId,
        provider_name: 'Google',
        image: profile.imageUrl,
        name: profile.name,
        roleType: roles.buyer,
      };
      const res: string = await authAction.socialLogin(payload);
      if (res)
        storeToken(res).then(() => {
          dispatch({ type: types.SHOW_REMODEL_ALERT, payload: true });
          history.push('/appraisal');
        });
    }
  };

  return (
    <div className="login_bg">
      <div className="container">
        <div className="pt-4 login_logo">
          <img alt="logo-png" src={logo} width={150} height={60} className="c-pointer" onClick={() => history.push('/')} />
        </div>

        <div className="login_wrapper">
          <div className="d-flex justify-content-between align-items-end">
            <div className="d-flex align-items-baseline justify-content-center">
              <div className="login_head">Sign In</div>
              <div className="login_head_buy">as a Buyer</div>
            </div>
            <Link to="/signup">
              <div className="login_text_action">Create an account</div>
            </Link>
          </div>

          <div>
            <input
              className={`saw-input form-control ${error.email ? 'is-invalid' : ''}`}
              type="email"
              autoComplete="email"
              placeholder="Email"
              value={loginDetails.email}
              onChange={(e) => {
                const value = e.target.value;
                setLoginDetails({ type: 'ON_TYPE', name: 'email', payload: value });
                validation.email(value);
              }}
            />
            {error.email && <div className="login_form_error">{error.email}</div>}
          </div>
          <div className=" password-container">
            <input
              className={`saw-input form-control ${error.password ? 'is-invalid' : ''}`}
              type={showPassword ? 'text' : 'password'}
              value={loginDetails.password}
              autoComplete="current-password"
              placeholder="Password"
              onChange={(e) => {
                const value = e.target.value;
                setLoginDetails({ type: 'ON_TYPE', name: 'password', payload: value });
                validation.password(value);
              }}
            />
            {loginDetails.password && <i className={'password-eye fa-lg theme-color c-pointer fa fa-eye' + (!showPassword ? '' : '-slash')} onClick={() => setShowPassword((v) => !v)} aria-hidden="true"></i>}
            {error.password && <div className="login_form_error">{error.password}</div>}
          </div>
          <div className="d-flex justify-content-end fs_14">
            <Link to="/forgot-password">
              <div className="login_text_action">Forgot password?</div>
            </Link>
          </div>

          <button className="login_button" onClick={handleFormSubmit}>
            Sign in
          </button>

          <GoogleLogin
            clientId={google_client_id}
            onSuccess={responseGoogle}
            render={(prop) => (
              <button className="login_with_google" onClick={prop.onClick} disabled={prop.disabled}>
                <span>
                  <img src={google} alt="" width={25} />
                </span>
                <span className="ml-2">Sign in with Google</span>
              </button>
            )}
          />

          <a href="https://market.saw.com/auth/sign-in">
            <div className="fs_16 login_text_action d-flex justify-content-center">Login as a Seller</div>
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
